// S14 My requests (root 6.12, REQ-9.2), phone-first: every report the person raised, across all apps,
// with its type (Bug report, Feature request) and its stage (never the status name) and when it last
// changed. A finished stage shows the outcome in the stage's place when it's shown to reporters: Won't
// fix, Shipped, Declined (poc CT-004).
// A Show filter narrows the list (poc CT-006, architecture 7.13): Pending (the default, no `?show`),
// Resolved (`?show=resolved`) or All (`?show=all`), each with its count, grouped in the browser from the
// list GET /api/my-requests returns (features/my-requests/groups.ts). The choice shown is remembered for
// the tab, so the status view's "← My requests" returns to it.
import type { MyRequestRow } from '@feedback/shared/contracts/query';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useEffect } from 'react';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { PageHeader } from '@/components/PageHeader';
import { RelativeTime } from '@/components/RelativeTime';
import { StageBadge } from '@/components/StageBadge';
import { parseShowSearch, requestGroup, SHOW_CHOICES, type Show, showSearch } from '@/features/my-requests/groups';
import { rememberShow } from '@/features/my-requests/last-show';
import { reporterTypeLabel } from '@/features/tickets/labels';
import { api } from '@/lib/api';
import { loadSession } from '@/lib/session';
import { cn } from '@/lib/utils';

const SHOW_LABELS: Record<Show, string> = { pending: 'Pending', resolved: 'Resolved', all: 'All' };

export const Route = createFileRoute('/my-requests/')({
  validateSearch: parseShowSearch,
  beforeLoad: ({ context, location }) => loadSession(context.queryClient, location.href),
  component: MyRequests,
});

function MyRequests() {
  const show: Show = Route.useSearch().show ?? 'pending';
  useEffect(() => {
    rememberShow(show);
  }, [show]);
  const requests = useQuery({
    queryKey: ['my-requests'],
    queryFn: async () => (await api.get<{ items: MyRequestRow[] }>('/api/my-requests')).items,
  });
  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="My requests" description="Everything you’ve reported, from every app." />
      {requests.isPending ? (
        <LoadingState label="Loading your requests" />
      ) : requests.isError ? (
        <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />
      ) : requests.data.length === 0 ? (
        <EmptyState title="You haven’t reported anything yet">
          Use the Feedback tab on any HPG app to report a problem or suggest an idea. You can follow it here.
        </EmptyState>
      ) : (
        <FilteredRequests rows={requests.data} show={show} />
      )}
    </div>
  );
}

function FilteredRequests({ rows, show }: { rows: MyRequestRow[]; show: Show }) {
  const byShow: Record<Show, MyRequestRow[]> = {
    pending: rows.filter((row) => requestGroup(row) === 'pending'),
    resolved: rows.filter((row) => requestGroup(row) === 'resolved'),
    all: rows,
  };
  const shown = byShow[show];
  return (
    <>
      <ShowFilter
        show={show}
        counts={{ pending: byShow.pending.length, resolved: byShow.resolved.length, all: rows.length }}
      />
      {shown.length === 0 ? (
        show === 'pending' ? (
          <EmptyState title="Nothing pending">
            Everything you’ve reported is resolved. Choose Resolved or All to see it.
          </EmptyState>
        ) : (
          <EmptyState title="Nothing resolved yet">
            Reports show here once they’re finished, such as a fix you’ve confirmed or a shipped request.
          </EmptyState>
        )
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-lg border border-border bg-surface-raised">
          {shown.map((request) => (
            <RequestRow key={request.publicId} request={request} />
          ))}
        </ul>
      )}
    </>
  );
}

/** Pending, Resolved, All: links, so the choice is in the URL (a refresh and the browser's Back keep it). */
function ShowFilter({ show, counts }: { show: Show; counts: Record<Show, number> }) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of links, not form controls, so not a fieldset
    <div role="group" aria-label="Show" className="mb-4 flex flex-wrap items-center gap-2">
      <span aria-hidden="true" className="text-sm text-muted">
        Show
      </span>
      <div className="flex w-fit flex-wrap gap-1 rounded-lg border border-border bg-surface p-1">
        {SHOW_CHOICES.map((choice) => (
          <Link
            key={choice}
            to="/my-requests"
            search={showSearch(choice)}
            // Exact, so Pending's link (no search) isn't also active under Resolved or All. TanStack marks the
            // active link aria-current="page" itself; the chosen one carries it too when the URL isn't canonical.
            activeOptions={{ exact: true }}
            aria-current={choice === show ? 'page' : undefined}
            data-testid={`my-requests-show-${choice}`}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium',
              choice === show ? 'bg-primary text-on-primary' : 'text-muted hover:text-text',
            )}
          >
            {/* Plain inline text, so the name reads "Pending 2". */}
            {SHOW_LABELS[choice]} <span className="tabular-nums">{counts[choice]}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function RequestRow({ request }: { request: MyRequestRow }) {
  return (
    <li>
      <Link
        to="/my-requests/$publicId"
        params={{ publicId: request.publicId }}
        className="flex items-center gap-3 p-3 hover:bg-surface"
        data-testid="my-request"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-sm text-muted">{request.reference ?? 'New'}</span>
            <span className="min-w-0 font-medium break-words">{request.title}</span>
          </p>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
            {request.type ? <span data-testid="my-request-type">{reporterTypeLabel(request.type)}</span> : null}
            <StageBadge stage={{ code: request.stage.code, name: request.outcome ?? request.stage.name }} />
            {request.withdrawnAt ? <span>Withdrawn</span> : null}
            <span>
              {request.app.name}, <RelativeTime iso={request.lastUpdatedAt} />
            </span>
          </p>
        </div>
        <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden="true" />
      </Link>
    </li>
  );
}
